import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { useTeam } from '../context/TeamContext';
import "react-datepicker/dist/react-datepicker.css";

function CreateRotaPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { teamMembers } = useTeam();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState([]);
  const [memberScarcityFactors, setMemberScarcityFactors] = useState({});
  const [selectedAlgorithm, setSelectedAlgorithm] = useState('weighted');

  const positions = ['Jimmy Jib', 'Selatan', 'Utara', 'Switcher', 'Title 1', 'Title 2', 'Animasi', 'Foto', 'Socmed'];
  const qualificationMap = {
    'Jimmy Jib': 'JimmyJib',
    'Selatan': 'Moving',
    'Utara': 'Moving',
    'Switcher': 'Switcher',
    'Title 1': 'Title',
    'Title 2': 'Title',
    'Animasi': 'Animasi',
    'Foto': 'Foto',
    'Socmed': 'Socmed'
  };
  const eventTypes = ['IBR1', 'IBR2', 'IBR3', 'IBR4', 'NOW', 'WBI', 'Wedding', 'Special Events'];
  
  useEffect(() => {
    if (location.state?.selectedDates) {
      const parsedDates = location.state.selectedDates.map(dateString => {
        return new Date(dateString);
      });
      setSelectedDates(parsedDates);
    }
    if (location.state?.selectedAlgorithm) {
      setSelectedAlgorithm(location.state.selectedAlgorithm);
    }
  }, [location.state]);

  useEffect(() => {
    const factors = {};
    teamMembers.forEach(member => {
      const preferredEventCount = eventTypes.filter(event => member.eventPreferences[event]).length;
      factors[member.id] = preferredEventCount > 0 ? 1 / preferredEventCount : 0;
    });
    setMemberScarcityFactors(factors);
  }, [teamMembers]);

  useEffect(() => {
    if (location.state?.isRegenerating) {
      generateRota();
    }
  }, [location.state?.isRegenerating]);

  const addDate = () => {
    if (!selectedDates.some(date => date.toDateString() === selectedDate.toDateString())) {
      setSelectedDates(prevDates => [...prevDates, selectedDate]);
    }
  };

  const removeDate = (dateToRemove) => {
    setSelectedDates(prevDates => prevDates.filter(date => date.toDateString() !== dateToRemove.toDateString()));
  };

  function generateRota() {
    if (selectedDates.length === 0) {
      console.error("No dates selected for rota generation");
      return;
    }

    const rota = {};
    const assignmentCounts = {};
    const memberPositionHistory = {}; // Track position assignments for diversity
    let davidSundayCount = 0;

    teamMembers.forEach(member => {
      assignmentCounts[member.id] = {};
      memberPositionHistory[member.id] = {}; // Initialize position history tracking
    });

    selectedDates.forEach(date => {
      const dayOfWeek = date.getDay();
      let events;
      switch (dayOfWeek) {
        case 0: events = ['IBR1', 'IBR2', 'IBR3', 'IBR4']; break;
        case 3: events = ['WBI']; break;
        case 5: events = ['NOW']; break;
        case 6: events = ['Wedding']; break;
        default: events = ['Special Events'];
      }
      events.forEach(event => {
        const eventKey = `${formatDateKey(date)} ${event}`;
        const isSunday = dayOfWeek === 0;
        rota[eventKey] = assignPositions(date, event, assignmentCounts, memberPositionHistory, isSunday, davidSundayCount);
        
        if (isSunday && Object.values(rota[eventKey]).includes('David')) {
          davidSundayCount++;
        }
      });
    });

    navigate('/generated-rota', { 
      state: { 
        generatedRota: rota, 
        selectedDates: selectedDates.map(date => date.toISOString()),
        selectedAlgorithm: selectedAlgorithm
      },
      replace: true
    });
  }

  function assignPositions(date, event, assignmentCounts, memberPositionHistory, isSunday, davidSundayCount) {
    const assignments = {};
    const dateString = formatDateKey(date);
    let availableMembers = teamMembers.filter(member => member.active);
  
    // Sort positions based on the number of qualified members
    const sortedPositions = getSortedPositions(availableMembers, event, dateString);
  
    sortedPositions.forEach(position => {
      const qualification = qualificationMap[position];
      const qualifiedMembers = availableMembers.filter(member => 
        member.qualifications[qualification] &&
        member.eventPreferences[event] &&
        !isUnavailable(member, dateString, event)
      );
  
      if (qualifiedMembers.length > 0) {
        const assigned = selectedAlgorithm === 'basic' 
          ? selectMemberWithBasicRandom(qualifiedMembers)
          : selectMemberWithWeightedRandom(qualifiedMembers, assignmentCounts, memberPositionHistory, position, isSunday, davidSundayCount, dateString, event);
        assignments[position] = assigned.name;
        
        // Update assignment counts
        if (!assignmentCounts[assigned.id][dateString]) {
          assignmentCounts[assigned.id][dateString] = 0;
        }
        assignmentCounts[assigned.id][dateString]++;
        
        // Update position history for diversity tracking
        if (!memberPositionHistory[assigned.id][position]) {
          memberPositionHistory[assigned.id][position] = 0;
        }
        memberPositionHistory[assigned.id][position]++;
        
        availableMembers = availableMembers.filter(member => member.id !== assigned.id);
      } else {
        assignments[position] = 'Unassigned';
      }
    });
  
    return assignments;
  }

  function getSortedPositions(availableMembers, event, dateString) {
    const positionCounts = positions.map(position => {
      const qualification = qualificationMap[position];
      const qualifiedCount = availableMembers.filter(member => 
        member.qualifications[qualification] &&
        member.eventPreferences[event] &&
        !isUnavailable(member, dateString, event)
      ).length;
      return { position, qualifiedCount };
    });

    return positionCounts
      .sort((a, b) => a.qualifiedCount - b.qualifiedCount)
      .map(item => item.position);
  }

  function selectMemberWithBasicRandom(members) {
    return members[Math.floor(Math.random() * members.length)];
  }

  function selectMemberWithWeightedRandom(members, assignmentCounts, memberPositionHistory, position, isSunday, davidSundayCount, dateString, event) {
    const weights = members.map(member => {
      let weight = (1 / (getTotalAssignments(assignmentCounts[member.id]) + 1)) * memberScarcityFactors[member.id];
      
      // Reduce weight if member already has 2 or more assignments on this date
      const dateAssignments = assignmentCounts[member.id][dateString] || 0;
      if (dateAssignments >= 2) {
        weight *= 0.5;
      }
      if (dateAssignments >= 3) {
        weight *= 0.25; // Further reduce weight for 3 or more assignments
      }

      // NEW: Apply position diversity factor for members with multiple qualifications
      const memberQualificationCount = countMemberQualifications(member);
      if (memberQualificationCount > 1) {
        const diversityFactor = calculatePositionDiversityFactor(member, memberPositionHistory, position);
        weight *= diversityFactor;
      }
  
      if (member.name === 'David' && (position === 'Title 1' || position === 'Title 2')) {
        if (isSunday && davidSundayCount < 2) {
          weight *= 3.5;
        }
        if (event === 'Special Events') {
          weight *= 3.5;
        }
      }
      
      return weight;
    });
  
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
    let randomValue = Math.random() * totalWeight;
    
    for (let i = 0; i < members.length; i++) {
      if (randomValue <= weights[i]) {
        return members[i];
      }
      randomValue -= weights[i];
    }
    
    return members[members.length - 1];
  }

  // NEW: Count how many qualifications a member has
  function countMemberQualifications(member) {
    return Object.values(member.qualifications).filter(hasQualification => hasQualification).length;
  }

  // NEW: Calculate diversity factor based on position history
  function calculatePositionDiversityFactor(member, memberPositionHistory, currentPosition) {
    const memberHistory = memberPositionHistory[member.id] || {};
    const currentPositionCount = memberHistory[currentPosition] || 0;
    
    // Get positions the member is qualified for
    const qualifiedPositions = positions.filter(pos => {
      const qualification = qualificationMap[pos];
      return member.qualifications[qualification];
    });
    
    if (qualifiedPositions.length <= 1) {
      return 1.0; // No diversity bonus if member only has one qualification
    }
    
    // Calculate average assignments across all qualified positions
    const totalAssignmentsInQualifiedPositions = qualifiedPositions.reduce((total, pos) => {
      return total + (memberHistory[pos] || 0);
    }, 0);
    
    const averageAssignmentsPerPosition = totalAssignmentsInQualifiedPositions / qualifiedPositions.length;
    
    // Apply diversity factor:
    // - If current position is assigned less than average, boost weight (encourage diversity)
    // - If current position is assigned more than average, reduce weight (discourage repetition)
    if (currentPositionCount <= averageAssignmentsPerPosition) {
      // Boost weight for positions assigned less than or equal to average
      const boostFactor = 1.3 + (averageAssignmentsPerPosition - currentPositionCount) * 0.2;
      return Math.min(boostFactor, 2.0); // Cap the boost at 2.0
    } else {
      // Reduce weight for positions assigned more than average
      const reductionFactor = 0.7 - (currentPositionCount - averageAssignmentsPerPosition) * 0.1;
      return Math.max(reductionFactor, 0.3); // Don't reduce below 0.3
    }
  }

  function getTotalAssignments(memberAssignments) {
    return Object.values(memberAssignments).reduce((sum, count) => sum + count, 0);
  }

  function isUnavailable(member, dateString, event) {
    return member.unavailableDates[dateString] && member.unavailableDates[dateString][event];
  }

  function formatDateKey(date) {
    if (!(date instanceof Date)) {
      console.warn('Invalid date object:', date);
      return 'Invalid Date';
    }
    return date.toISOString().split('T')[0];
  }

  return (
    <div className="page-container">
      <h1 className="text-3xl font-bold text-gray-800 text-center mb-8">CREATE ROTA</h1>
      
      {/* Algorithm Selection */}
      <div className="space-y-4 mb-8">
        <h2 className="text-xl font-semibold text-gray-700">Algorithm Selection:</h2>
        <div className="space-y-3">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              value="basic"
              checked={selectedAlgorithm === 'basic'}
              onChange={() => setSelectedAlgorithm('basic')}
              className="w-4 h-4 text-primary-500 border-gray-300 focus:ring-primary-500"
            />
            <span className="text-gray-700">Basic Algorithm</span>
          </label>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              value="weighted"
              checked={selectedAlgorithm === 'weighted'}
              onChange={() => setSelectedAlgorithm('weighted')}
              className="w-4 h-4 text-primary-500 border-gray-300 focus:ring-primary-500"
            />
            <span className="text-gray-700">Weighted Algorithm (with scarcity prioritization & position diversity)</span>
          </label>
        </div>
      </div>

      {/* Date Selection */}
      <div className="space-y-4 mb-8">
        <label htmlFor="date-picker" className="block text-xl font-semibold text-gray-700">
          Select dates:
        </label>
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <DatePicker
            id="date-picker"
            selected={selectedDate}
            onChange={date => setSelectedDate(date)}
            dateFormat="MMMM d, yyyy"
            className="form-input w-full sm:w-auto"
            placeholderText="Select a date"
          />
          <button 
            onClick={addDate}
            className="btn-primary whitespace-nowrap"
          >
            Add Date
          </button>
        </div>
      </div>

      {/* Selected Dates Display */}
      {selectedDates.length > 0 && (
        <div className="space-y-4 mb-8">
          <h2 className="text-xl font-semibold text-gray-700">Selected Dates:</h2>
          <div className="bg-gray-50 rounded-lg p-4">
            <ul className="space-y-2">
              {selectedDates.map((date, index) => (
                <li key={index} className="flex justify-between items-center bg-white p-3 rounded border">
                  <span className="font-medium text-gray-800">{formatDateKey(date)}</span>
                  <button 
                    onClick={() => removeDate(date)}
                    className="text-red-500 hover:text-red-700 text-sm px-3 py-1 rounded transition-colors"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Generate Button */}
      <div className="text-center">
        <button 
          onClick={generateRota} 
          disabled={selectedDates.length === 0} 
          className={`btn-primary py-3 px-8 text-lg ${
            selectedDates.length === 0 
              ? 'opacity-50 cursor-not-allowed' 
              : 'hover:bg-primary-600'
          }`}
        >
          {location.state?.isRegenerating ? 'Regenerate Rota' : 'Generate Rota'}
        </button>
      </div>
    </div>
  );
}

export default CreateRotaPage;